import { useTranslation } from "react-i18next";
import { Avatar, Box, Button, Chip, Collapse, Divider, Grid, IconButton, InputAdornment, ListItem, ListItemAvatar, ListItemText, Stack, TextField, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useCallback, useEffect, useMemo } from "react";
import { Search as SearchIcon, ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import Scrollbar from "../../../common/core/Scrollbar";
import { FormatLocation } from "../../../../common/maphelpers";
import { FormatDistance } from "../../navigation/navcore/common/helpers";

const useStyles = makeStyles()(
  (theme) => ({
    stationItem: {
      width: "100%",
      background: theme.palette.background.contentCardGrey,
      borderRadius: theme.spacing(1),
      border: `1px solid ${theme.palette.common.borderSuperSoft}`,
      cursor: "default",
      position: "relative",
      "&:hover": {
        "&:after": {
          zIndex: 1000,
          position: "absolute",
          height: "100%",
          width: "100%",
          top: 0,
          left: 0,
          background: theme.palette.action.hover,
          borderRadius: "8px",
          content: "''",
          display: "block",
          pointerEvents: "none"
        },
      }
    },
    table: {
      width: "100%",
      "& td": {
        verticalAlign: "top"
      },
      "& tr > :first-of-type": {
        width: "128px"
      }
    },
    subheader: {
      marginTop: theme.spacing(1)
    },
    listItemContent: {
      padding: theme.spacing(2),
      paddingTop: 0
    },
    scrollbar: {
      height: "750px",
      maxHeight: "calc(75vh - 180px)"
    },
    scrollbarBox: {
      marginLeft: `calc(0px - var(--scrollbar-width) ) !important`,
      marginRight: `calc(0px - var(--scrollbar-width) ) !important`
    }
  })
);

export default function NetworkStationSearch(props) {
  const { onSelected, query, setQuery, filteredStations, expanded, setExpanded } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();

  const onExpand = useCallback((station) => {
    setExpanded(station.id);
  }, []);

  useEffect(() => {
    if (filteredStations.length === 1) {
      setExpanded(filteredStations[0].id);
    } else if (filteredStations.length === 0) {
      setExpanded("");
    } else if (!!expanded && !filteredStations.find(t => t.id === expanded)) {
      setExpanded("");
    }
  }, [filteredStations, expanded]);

  return (
    <Box>
      <Stack spacing={2}>

        <TextField
          label={t("missionRequests:networkStationSearch:input")}
          variant="filled"
          value={query}
          onChange={ev => setQuery(ev.target.value)}
          fullWidth
          size="small"
          type="text"
          slotProps={{
            input: {
              disableUnderline: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              )
            }
          }}
        />

        <Divider />

        <Typography variant="subtitle1">{!!query ? t("missionRequests:networkStationSearch:searchResults") : t("missionRequests:networkStationSearch:closeStations")}</Typography>

        <Box className={classes.scrollbarBox}>
          <Scrollbar className={classes.scrollbar}>
            <Stack spacing={1}>
              {
                useMemo(
                  () => filteredStations.map(
                    (station, key) =>
                      <StationItem station={station} key={key} expanded={station.id === expanded} onExpand={onExpand} onSelected={onSelected} />
                  ),
                  [filteredStations, onExpand, expanded, onSelected]
                )
              }
            </Stack>
          </Scrollbar>
        </Box>

      </Stack>
    </Box>
  );
}


export function StationItem(props) {
  const { station, expanded, onExpand, onSelected } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const onHandleSelect = useCallback((ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    onSelected(station)
  }, [station, onSelected])


  return (
    <Box
      className={classes.stationItem}
      onClick={onExpand !== undefined ? () => onExpand(station) : undefined}

    >
      <ListItem
        alignItems="flex-start"
        className={classes.listItem}
      >
        <ListItemAvatar>
          <Avatar alt={station.displayName} src={station.logo} variant="rounded" />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">
                {station.displayName}
              </Typography>
              {!!station.distance && station.distance > 100 ? (
                <Typography variant="body2" color="textSecondary">
                  {FormatDistance(station.distance / 1000.0)} km
                </Typography>
              ) : null}
            </Stack>
          }
          secondary={
            <Typography
              component="span"
              variant="body2"
              color="textSecondary"
            >
              {FormatLocation(station.location)}
            </Typography>
          }
        />
      </ListItem>
      <Collapse in={expanded}>
        <Box className={classes.listItemContent}>
          <Stack spacing={1} justifyContent={"space-between"}>

            {
              typeof onSelected === "function" ? (
                <Button fullWidth color="primary" variant="contained" endIcon={<ChevronRightIcon />} onClick={onHandleSelect}>{t("missionRequests:networkStationSearch:select")}</Button>
              ) : null
            }

            <Typography variant="body2">{station.description}</Typography>

            <table className={classes.table}>
              <tbody>
                <tr>
                  <td><Typography variant="subtitle2" className={classes.subheader}>{t("missionRequests:networkStationSearch:contactInfo")}</Typography></td>
                </tr>

                <tr>
                  <td><Typography variant="body2" color="textSecondary">{t("common:email")}</Typography></td>
                  <td><Typography variant="body2">{station.supportEmail || "-"}</Typography></td>
                </tr>

                <tr>
                  <td><Typography variant="body2" color="textSecondary">{t("entity:location:address")}</Typography></td>
                  <td><Typography variant="body2">{station.location?.address || "-"}</Typography></td>
                </tr>

                <tr>
                  <td><Typography variant="body2" color="textSecondary">{t("common:phoneNumber")}</Typography></td>
                  <td><Typography variant="body2">{station.supportPhone || "-"}</Typography></td>
                </tr>
              </tbody>
            </table>

            <Box>
              <Typography variant="subtitle2" className={classes.subheader}>{t("missionRequests:networkStationSearch:services")}</Typography>
            </Box>

            <Box>
              <Grid container spacing={0.5}>
                {station.services.map((service, key) => (
                  <Grid item key={key}>
                    <Chip label={service.name} size="small" />
                  </Grid>
                ))}
              </Grid>

            </Box>



          </Stack>
        </Box>
      </Collapse>
    </Box>
  )
}

